import { LOCAL_STORAGE_DATA_KEY } from '../constants/detialsDevice';
import { SIM_IMG_URL } from '../constants/images';
import { PROMO_TOOLTIP_BUTTON_TEXT } from '../constants/promo';
import { formatPrice } from '../utils/formatting';
import getLocalCurrencyStr from '../utils/getLocalCurrencyStr';

interface IBadge {
  title: string;
  content: string;
  variant?: string;
  textColor?: string;
}

type ExtraProduct = {
  id: number;
  name: string;
  price: number;
  priceFormatted: string;
  oldPrice: number;
  oldPriceFormatted: string;
  kind: number;
  kindFormatted: string;
  legal: null;
  imageUrl: string;
  hasESim: boolean | null;
};

export interface IDeviceModal {
  productId: string;
  isPreorder: boolean;
  isOutOfStock: boolean;
  productName: string;
  brandName: string;
  baseUrl: string;
  characteristics: {
    brief: string;
  };
  badges: IBadge[];
  isFlocktory?: boolean;
  impression: {
    brand: string;
    category: number;
    id: number;
    list: string;
    name: string;
    position: number | null;
    price: string;
    variant: string;
  };
  article: string;
  images: {
    alt: string;
    id: string;
    imageUrl: string;
    shopProductId: number;
    thumbUrl: string;
    weight: number;
    widgetImageUrl: string;
  }[];
  types: {
    list: {
      href: string;
      isActive: boolean;
      productSlug: string;
      title: string;
    }[];
    title: string;
    titleShort: string;
  }[];
  colors: {
    color: string;
    href: string;
    isActive: boolean;
    productSlug: string;
    title: string;
  }[];
  productSlug: string;
  feedback: {
    agreement: string;
    average: number;
    changeUsefulnessUrl: string;
    count: number;
    legacyModelUrl: string;
    list: [];
    loadMoreUrl: string;
    maxCommentLength: number;
    mostUseful: any;
    newRatedCookieName: string;
    ratedCookieName: string;
    sortBy: number;
    submitErrorTemplate: string;
    submitSuccessTemplate: string;
    submitUrl: string;
  };
  promotion: {
    available: {
      name: string;
      isOnlyWithPromo: boolean;
      valuePreposition: boolean;
      priceWithoutPromotion: number;
      bundlePrice: number;
      discountPercent: number;
      extraProducts: ExtraProduct[];
    };
  };
  contentPromotion: string;
  price: {
    discountPercent: number;
    oldValue: number;
    priceComment: string;
    value: number;
  };
  creditDetails: {
    availableCredit: boolean;
    buttonText: string;
    installment: {
      buttonText: string;
      callToAction: string;
      title: string;
    };
    minPayment: number;
    referenceTemplate: string;
    slug: string;
    title: string;
  };
  bnpl?: {
    available: boolean;
    bnplPayment: number;
    bnplPaymentCount: number;
    content: {
      shopProductCartShortInfo: string;
      shopBnplText: string;
      shopBnplToolTip: string;
      fullInformationUrl: string;
      imageUrl: string;
      isBnplFeatureShown: boolean;
    };
  };
  cjBundle?: boolean;
}

interface IPromoBadge {
  children: string;
  help: string;
  variant?: string;
  textColor?: string;
}

export interface IDeviceBuilder {
  isProductDetails: boolean;
  id: string;
  contentType: string;
  name: string;
  brandName: string;
  link: string;
  characteristics: string;
  promoBadges: IPromoBadge | never[];
  isFlocktory: boolean;
  isInBasket: boolean;
  brand: string;
  article: string;
  categoryId: string;
  isPreorder: boolean;
  imgOptions: {
    src: string;
    alt: string;
  }[];
  capacity:
    | {
        value: string;
        label: string;
        isActive: boolean;
      }[]
    | null
    | undefined;
  types:
    | {
        title: string;
        list: {
          slug: string;
          title: string;
          isActive: boolean;
          id: string;
        }[];
      }[]
    | null;
  color: {
    value: string;
    label: string;
    isActive: boolean;
    title: string;
  }[];
  isOutOfStock: boolean;
  reviews: {
    count: number;
    average: number;
    link: string;
  };
  promoSubscribe: string | undefined;
  promoSaleSoon: string | undefined;
  personalDataLink: string | undefined;
  promoBundle: {
    available: boolean;
    extraProducts: { imageUrl: string; name: string }[];
    onClick: () => void;
    price: number;
    oldPrice: number;
    discount: number;
    label: string;
    link: string;
  } | null;
  isNewBundle: boolean;
  promoCode: {
    label: string;
    link: string;
  } | null;
  price: number;
  oldPrice: number | undefined;
  discount: number;
  isSended: boolean;
  promoCredit: {
    label: string;
    link: string;
  } | null;
  isFavorite: boolean;
  isCmp: boolean;
  tShirtSize:
    | {
        value: string;
        label: string;
        isActive: boolean;
      }[]
    | null
    | undefined;
  flocktoryId: string;
  installments: {
    primaryText: string;
    secondaryText: string;
    tooltipMessage: string;
    tooltipButtonText: string;
    tooltipButtonHref: string;
  }[];
}

/**
 * TODO
 * Данный класс используется для маппинга данных запроса /shop/popupdetails/{slug} в нужный
 * формат для компонента DeviceModal из пакета @beef/smart-kit
 * На совместной встрече с командой Нонтелко было выработано решение создать
 * общий промежуточный слой для маппинга данных всех запросов.
 * Поэтому данный код нужно будет перенести в промежуточный слой после выполнения задачи
 * https://servicedesk.veon.com/browse/ES-4517
 * @deprecated
 */
export class DevicePreviewBuilder {
  data = {} as IDeviceBuilder;

  setDeviceId(device: IDeviceModal) {
    this.data.id = device.productId;

    return this;
  }

  setFlocktoryId(device: IDeviceModal, currentRegionId: number) {
    this.data.flocktoryId = `${currentRegionId}_${device.productId}`;

    return this;
  }

  setIsProductDetails(isProducetDetails: boolean) {
    this.data.isProductDetails = isProducetDetails;
  }

  setDeviceContentType(device: IDeviceModal) {
    let contentType = 'preview';
    if (device?.isPreorder) {
      contentType = 'preOrder';
    }

    if (device?.isOutOfStock) {
      contentType = 'subscribePreview';
    }

    this.data.contentType = contentType;

    return this;
  }

  setDeviceName(device: IDeviceModal) {
    this.data.name = device.productName;

    return this;
  }

  setDeviceBrandName(device: IDeviceModal) {
    this.data.brandName = device.brandName;

    return this;
  }

  setDeviceLink(device: IDeviceModal) {
    this.data.link = device.baseUrl;

    return this;
  }

  setDeviceCharacteristics(device: IDeviceModal) {
    this.data.characteristics = device?.characteristics?.brief;

    return this;
  }

  setDevicePromoBadges(device: IDeviceModal) {
    const badges = device?.badges ?? [];
    this.data.promoBadges = badges.reduce((acc, cur) => {
      let badge = {
        children: cur.title,
        help: cur.content,
      } as IPromoBadge;

      if (cur.variant) {
        badge = {
          ...badge,
          variant: cur.variant,
        };
      }

      if (cur.textColor) {
        badge = {
          ...badge,
          textColor: cur.textColor,
        };
      }

      acc.push(badge as never);
      return acc;
    }, []);

    return this;
  }

  setDeviceIsFlocktory(isFlocktory: boolean) {
    this.data.isFlocktory = isFlocktory;

    return this;
  }

  setDeviceIsInBasket(device: IDeviceModal, basketProductIds: string[]) {
    const basketProductIdsSet = new Set(basketProductIds);

    this.data.isInBasket = basketProductIdsSet.has(device.productId.toString());

    return this;
  }

  setDeviceIsFavorite(device: IDeviceModal, favoriteList: string[]) {
    const favoriteListSet = new Set(favoriteList);

    this.data.isFavorite = favoriteListSet.has(device.productId);

    return this;
  }

  setDeviceIsCmp(device: IDeviceModal, CmpList: string[]) {
    const cmpListSet = new Set(CmpList);
    this.data.isCmp = cmpListSet.has(device.productId);

    return this;
  }

  setDeviceBrand(device: IDeviceModal) {
    this.data.brand = device.impression.brand;

    return this;
  }

  setDeviceArticle(device: IDeviceModal) {
    this.data.article = device.article;

    return this;
  }

  setDeviceCategoryId(device: IDeviceModal) {
    this.data.categoryId = device.impression.category.toString();

    return this;
  }

  setDeviceIsPreorder(device: IDeviceModal) {
    this.data.isPreorder = device.isPreorder;

    return this;
  }

  setDeviceImgOptions(device: IDeviceModal) {
    const imgOptions = device.images.map((image) => ({
      src: image.imageUrl,
      alt: image.alt,
    }));

    this.data.imgOptions = imgOptions;

    return this;
  }

  setDeviceCapacity(device: IDeviceModal) {
    const capacitySize =
      device?.types && device?.types?.find((type) => type?.title === 'Объем встроенной памяти');
    const capacity = capacitySize?.list?.map((type) => ({
      value: type?.productSlug,
      label: type?.title,
      isActive: type?.isActive,
    }));

    this.data.capacity = (capacity?.length || 0) > 0 ? capacity : null;
    return this;
  }

  setDeviceTypes(device: IDeviceModal) {
    const deviceTypes = (device?.types || []).map((type) => ({
      title: type?.titleShort || type?.title,
      list: (type.list || []).map((li) => ({
        slug: li?.productSlug,
        title: li?.title,
        isActive: li?.isActive,
        id: li?.productSlug,
      })),
    }));
    this.data.types = deviceTypes.length > 0 ? deviceTypes : null;
    return this;
  }

  setDeviceTShirtType(device: IDeviceModal) {
    const tShirtType = device?.types && device?.types?.find((type) => type?.title === 'Размер');
    const tShirtSize = tShirtType?.list?.map((type) => ({
      value: type?.productSlug,
      label: type?.title,
      isActive: type?.isActive,
    }));

    this.data.tShirtSize = (tShirtSize?.length || -1) > 0 ? tShirtSize : null;

    return this;
  }

  setDeviceColor(device: IDeviceModal) {
    const colors = device?.colors ?? [];
    const deviceColors = colors.map(({ href, color, isActive, title }) => ({
      value: href,
      label: color,
      isActive,
      title,
    }));

    this.data.color = deviceColors;

    return this;
  }

  setDeviceIsOutOfStock(device: IDeviceModal) {
    this.data.isOutOfStock = device.isOutOfStock;

    return this;
  }

  setDeviceReviews(device: IDeviceModal) {
    const reviews = {
      count: device.feedback?.count,
      average: device.feedback?.average,
      link: `/shop/details/${device?.productSlug}/otzyvy`,
    };

    this.data.reviews = reviews;

    return this;
  }

  setDevicePromoSubscribe(device: IDeviceModal) {
    const promoSubscribe =
      device.isOutOfStock ?
        'Оставьте свой e-mail, и мы уведомим вас при поступлении товара'
      : undefined;

    this.data.promoSubscribe = promoSubscribe;

    return this;
  }

  setDevicePromosubscribeSaleSoon(device: IDeviceModal) {
    const promoSaleSoon = device.isOutOfStock ? 'Товара нет в наличии' : undefined;

    this.data.promoSaleSoon = promoSaleSoon;

    return this;
  }

  setDevicePromosubscribeSalePersonalDataLink(device: IDeviceModal) {
    const personalDataLink =
      device.isOutOfStock ? '//static.beeline.ru/upload/images/soglasia_27122017.pdf' : undefined;

    this.data.personalDataLink = personalDataLink;

    return this;
  }

  setDevicePromoBundle(device: IDeviceModal) {
    const available = !!(
      device.promotion?.available?.name &&
      device.promotion?.available?.extraProducts &&
      device.promotion?.available.priceWithoutPromotion &&
      device.promotion?.available.bundlePrice &&
      device.promotion?.available.discountPercent
    );

    this.data.isNewBundle = !!device?.cjBundle;
    this.data.promoBundle =
      device.promotion?.available ?
        {
          available,
          label: device.promotion?.available?.name,
          link: `${device.baseUrl}#promo`,
          extraProducts: DevicePreviewBuilder.composeExtraProducts(
            device.promotion?.available?.extraProducts,
          ),
          oldPrice: device.promotion?.available.priceWithoutPromotion,
          price: device.promotion?.available.bundlePrice,
          discount: device.promotion?.available.discountPercent,
          onClick: () => {
            window.location.href = `${device.baseUrl}#promo`;
          },
        }
      : null;

    return this;
  }

  setDevicePromoCode(device: IDeviceModal) {
    const promoCode =
      device.contentPromotion ?
        {
          label: device.contentPromotion || '',
          link: `${device.baseUrl}#promo`,
        }
      : null;

    this.data.promoCode = promoCode;

    return this;
  }

  setDevicePrice(device: IDeviceModal) {
    // https://servicedesk.veon.com/browse/ES-554 корректное отображение цены
    const isBundleState = !!device?.promotion;
    const isPromo = device?.promotion?.available?.isOnlyWithPromo;
    const isValuePreposition = device?.promotion?.available?.valuePreposition;
    const isBundlePrice = isBundleState && !isValuePreposition && isPromo;

    let price = device?.price?.value;

    if (isValuePreposition || isBundlePrice) {
      price = device.promotion.available.bundlePrice;
    }

    this.data.price = price;

    return this;
  }

  setDeviceOldPrice(device: IDeviceModal) {
    // https://servicedesk.veon.com/browse/ES-554 корректное отображение цены
    const isBundleState = !!device?.promotion;
    const isPromo = device?.promotion?.available?.isOnlyWithPromo;
    const isValuePreposition = device?.promotion?.available?.valuePreposition;
    const isBundlePrice = isBundleState && !isValuePreposition && isPromo;

    let price = device?.price?.value;
    const oldPrice = device?.price?.oldValue;

    if (isValuePreposition || isBundlePrice) {
      price = device.promotion.available.bundlePrice;
    }

    const deviceOldPrice = oldPrice === price ? undefined : oldPrice;

    this.data.oldPrice = deviceOldPrice;

    return this;
  }

  setDeviceDiscount(device: IDeviceModal) {
    const isPromo = device?.promotion?.available?.isOnlyWithPromo;

    const discount =
      isPromo ? device.promotion.available.discountPercent : device?.price?.discountPercent;

    this.data.discount = discount;

    return this;
  }

  setDeviceIsSended(device: IDeviceModal) {
    let isSended = false;

    const outofstockList =
      JSON.parse(window.localStorage.getItem(LOCAL_STORAGE_DATA_KEY) || '[]') ?? [];
    if (outofstockList.includes(device.productId)) {
      isSended = true;
    }

    this.data.isSended = isSended;

    return this;
  }

  setDevicePromoCredit(device: IDeviceModal) {
    const label = device.creditDetails?.referenceTemplate
      .replace('<AnchorLink>', '')
      .replace('</AnchorLink>', '')
      .replace('₽', '')
      .replace('{{minPayment}}', getLocalCurrencyStr(device.creditDetails?.minPayment));

    const promoCredit =
      device.creditDetails ?
        {
          label,
          link: `${device.baseUrl}#payment`,
        }
      : null;

    this.data.promoCredit = promoCredit;

    return this;
  }

  setBnpl(device: IDeviceModal) {
    const available = device.bnpl?.available;
    const bnplPayment = device.bnpl?.bnplPayment || '';
    const bnplPaymentCount = device.bnpl?.bnplPaymentCount || '4';
    const content = device.bnpl?.content;
    const isBnplFeatureShown = content?.isBnplFeatureShown;

    this.data.installments =
      available && isBnplFeatureShown ?
        [
          {
            primaryText: content?.shopBnplText || '',
            secondaryText:
              bnplPayment && bnplPaymentCount ?
                `${formatPrice(bnplPayment)} ₽ × ${bnplPaymentCount}`
              : '',
            tooltipMessage: content?.shopProductCartShortInfo || '',
            tooltipButtonText: PROMO_TOOLTIP_BUTTON_TEXT,
            tooltipButtonHref: `${device.baseUrl}#payment`,
          },
        ]
      : [];

    return this;
  }

  getDeviceData() {
    return this.data;
  }

  static composeExtraProducts(extraProducts: ExtraProduct[] = []) {
    return extraProducts.map((extra) => {
      const name = extra.kindFormatted === 'Tariff' ? `Тариф «${extra.name}»` : extra.name;
      const imageUrl = extra.kindFormatted === 'Tariff' ? SIM_IMG_URL : extra.imageUrl;

      return { name, imageUrl };
    });
  }
}
